import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from '../../../utils/cn';

const base =
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-xl border font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/25 disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 transition-opacity duration-100 ease-in-out cursor-pointer';
const variant = {
  dark: '!border-transparent bg-foreground text-background hover:bg-foreground/85 focus:bg-foreground/85 disabled:bg-foreground/20 disabled:text-background/80',
  secondary:
    'border-border bg-card text-foreground hover:bg-accent hover:no-underline focus-visible:border-foreground disabled:bg-muted disabled:text-foreground-subtle',
  primary_dark_light:
    '!border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80 disabled:bg-muted disabled:text-foreground-subtle',
  white:
    'border-border bg-background text-foreground hover:bg-accent disabled:bg-muted disabled:text-foreground-subtle',
  danger:
    'border-destructive bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:border-destructive-muted disabled:bg-destructive-muted disabled:text-destructive-foreground/70',
  success:
    'border-success bg-success text-success-foreground hover:bg-success/90 disabled:border-success-muted disabled:bg-success-muted disabled:text-success-foreground/70',
  link: '!border-transparent bg-transparent text-foreground hover:opacity-55 hover:no-underline disabled:text-foreground-subtle',
  ghost: '!border-transparent bg-transparent text-muted-foreground hover:bg-accent',
  /* Subtle filled chip: muted gray bg + faded foreground label that darkens
     (or brightens, in dark mode) to full foreground on hover. Uses an
     alpha modifier on `foreground` rather than `muted-foreground` so the
     hover delta is visually consistent across themes — swapping
     `muted-foreground` → `foreground` left too small a gap in dark mode
     where both values are light grays.
     Useful for secondary in-surface triggers (e.g. form helpers, overlaid
     dropdown triggers) where a stronger `secondary` outlined chip or
     transparent `ghost` button would both feel wrong. */
  muted:
    '!border-transparent bg-muted text-foreground/60 hover:text-foreground disabled:text-foreground-subtle',
  outline: 'border-border bg-transparent text-foreground hover:bg-accent',
  custom: '',
};

const buttonVariants = cva(base, {
  variants: {
    variant,
    size: {
      icon: 'h-[34px] w-[34px] px-2 py-2 text-sm',
      xxs: 'h-[20px] px-1.5 py-2 text-xs rounded-lg',
      xs: 'h-[26px] px-1.5 py-1.5 text-xs rounded-lg',
      sm: 'h-[32px] px-2.5 py-2 text-sm',
      md: 'h-[41px] px-5 py-2.5 text-sm',
      lg: 'h-[48px] px-5 py-3 text-base',
      xl: 'h-[50px] px-6 py-3.5 text-base',
    },
  },
  defaultVariants: {
    variant: 'dark',
    size: 'md',
  },
});

const iconButtonVariants = cva(base, {
  variants: {
    variant,
    size: {
      xxs: 'h-[20px] w-[20px] p-1 text-xs',
      xs: 'h-[26px] w-[26px] p-1.5 text-xs rounded-md',
      sm: 'h-[32px] w-[32px] p-2 text-sm',
      md: 'h-[41px] w-[41px] text-sm',
      lg: 'h-[48px] w-[48px] p-3 text-base',
      xl: 'h-[50px] w-[50px] p-3.5 text-base',
    },
  },
  defaultVariants: {
    variant: 'dark',
    size: 'md',
  },
});

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className }),
          'disabled:opacity-50 transition-opacity duration-300 ease-in-out',
        )}
        ref={ref}
        type="button"
        {...props}
      />
    );
  },
);

export interface IconButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof iconButtonVariants> {
  asChild?: boolean;
}

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(
          iconButtonVariants({ variant, size, className }),
          'disabled:opacity-50 transition-opacity duration-300 ease-in-out',
        )}
        ref={ref}
        type="button"
        {...props}
      />
    );
  },
);

export { Button, IconButton, buttonVariants, iconButtonVariants };
